import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

type TypographyVariant =
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p1' | 'p2' | 'p3' | 'p4';
type TypographyType = 'text' | 'link';
/** Intrinsic tag or component (e.g. react-router `Link`). */
type TypographyAs = React.ElementType;
type TypographyColor =
  | 'foreground'
  | 'primary'
  | 'secondary'
  | 'muted'
  | 'destructive'
  | 'accent'
  | 'brand'
  | 'brandAccent'
  | 'danger'
  | 'warning'
  | 'success'
  | 'info';

/** Numeric weights map to Tailwind font utilities; overrides variant default weight when set. */
type TypographyFontWeight = 400 | 500 | 600 | 700;

interface TypographyProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'color'
> {
  variant?: TypographyVariant;
  type?: TypographyType;
  /** Explicit semantic element override (e.g. `span`, `div`, `label`). */
  as?: TypographyAs;
  /** React Router internal path; with `type="link"` renders `<Link>` instead of `<a>`. */
  to?: string;
  color?: TypographyColor;
  /** Text color on hover; uses the same tokens as `color` → `hover:text-*`. */
  hoverColor?: TypographyColor;
  /** When set, overrides the font weight from `variant` (e.g. 700 → `font-bold`). */
  fontWeight?: TypographyFontWeight;
  href?: string;
  target?: string;
  rel?: string;
}

const VARIANT_CLASSES: Record<TypographyVariant, string> = {
  h1: 'text-4xl font-extrabold leading-[44px]',
  h2: 'text-3xl font-bold leading-[38px]',
  h3: 'text-2xl font-semibold leading-8',
  h4: 'text-xl font-semibold leading-7',
  h5: 'text-lg font-semibold leading-[26px]',
  h6: 'text-base font-semibold leading-6',
  p1: 'text-lg font-normal leading-7',
  p2: 'text-base font-normal leading-6',
  p3: 'text-sm font-normal leading-5',
  p4: 'text-xs font-normal leading-[18px]',
};

const COLOR_CLASSES: Record<TypographyColor, string> = {
  foreground: 'text-foreground',
  primary: 'text-primary',
  secondary: 'text-secondary-foreground',
  muted: 'text-muted-foreground',
  destructive: 'text-destructive',
  accent: 'text-accent-foreground',
  brand: 'text-[var(--brand-primary)]',
  brandAccent: 'text-[var(--brand-accent)]',
  danger: 'text-red-400',
  warning: 'text-orange-400',
  success: 'text-green-400',
  info: 'text-blue-400',
};

// Static map so Tailwind JIT can detect the full class strings at build time.
const HOVER_COLOR_CLASSES: Record<TypographyColor, string> = {
  foreground: 'hover:text-foreground',
  primary: 'hover:text-primary',
  secondary: 'hover:text-secondary-foreground',
  muted: 'hover:text-muted-foreground',
  destructive: 'hover:text-destructive',
  accent: 'hover:text-accent-foreground',
  brand: 'hover:text-[var(--brand-primary)]',
  brandAccent: 'hover:text-[var(--brand-accent)]',
  danger: 'hover:text-red-400',
  warning: 'hover:text-orange-400',
  success: 'hover:text-green-400',
  info: 'hover:text-blue-400',
};

const FONT_WEIGHT_CLASSES: Record<TypographyFontWeight, string> = {
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
};

const LINK_CLASSES =
  'no-underline cursor-pointer transition-[text-decoration_0.15s_ease] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-sm';

// Map variant → semantic HTML tag for type="text"
const VARIANT_TAG_MAP: Record<TypographyVariant, keyof JSX.IntrinsicElements> =
  {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    p1: 'p',
    p2: 'p',
    p3: 'p',
    p4: 'p',
  };

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = 'p2',
      type = 'text',
      as,
      to,
      color = 'foreground',
      hoverColor,
      fontWeight,
      href,
      target,
      rel,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // Auto-apply rel="noopener noreferrer" when target="_blank" and no explicit rel
    const resolvedRel =
      target === '_blank' && !rel ? 'noopener noreferrer' : rel;

    const classNames = cn(
      'm-0 p-0',
      VARIANT_CLASSES[variant],
      COLOR_CLASSES[color],
      hoverColor != null && HOVER_COLOR_CLASSES[hoverColor],
      hoverColor != null && 'transition-colors',
      fontWeight != null && FONT_WEIGHT_CLASSES[fontWeight],
      type === 'link' && LINK_CLASSES,
      className,
    );

    // Render React Router <Link> when type="link" and `to` is provided
    if (type === 'link' && to) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classNames}
          {...props}
        >
          {children}
        </Link>
      );
    }

    // Render <a> when type="link"
    if (type === 'link') {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={resolvedRel}
          className={classNames}
          {...props}
        >
          {children}
        </a>
      );
    }

    // Render explicit element override (e.g. span, div, label)
    if (as) {
      const AsTag = as as React.ElementType;
      return (
        <AsTag ref={ref} className={classNames} {...props}>
          {children}
        </AsTag>
      );
    }

    // Render semantic heading or paragraph element
    const Tag = VARIANT_TAG_MAP[variant] as React.ElementType;

    return (
      <Tag ref={ref} className={classNames} {...props}>
        {children}
      </Tag>
    );
  },
);

Typography.displayName = 'Typography';

export { Typography };
export type {
  TypographyProps,
  TypographyVariant,
  TypographyType,
  TypographyAs,
  TypographyColor,
  TypographyFontWeight,
};
